import React, { useEffect, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Row, Col, Label, Input, FormGroup } from 'reactstrap';
import Select from 'react-select';
import { raceKitFiltersDefault, raceKitStatusOptions, raceKitNotesOptions } from '../../app/auth/raceKits/state';

/**
 * Filters for the race kits grid.
 *
 * Edits are held locally and only handed back on Apply, so half-built criteria
 * never reach the grid and Cancel genuinely discards. Reset clears to "no
 * filters", which is the page's load state.
 */
export const RaceKitFilterModal = ({ isOpen, toggle, filters, onApply, raceOptions, clubOptions, teeSizeOptions, clubsLoading, canSeeNotes }) => {
    const [draft, setDraft] = useState(filters)

    // Reseed whenever it opens: the user may have reset or applied since.
    useEffect(() => {
        if(isOpen) setDraft(filters)
    }, [isOpen, filters])

    const set = (patch) => setDraft((prev) => ({ ...prev, ...patch }))

    const selected = (options, values) => options.filter((o) => values.includes(o.value))

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg" scrollable>
            <ModalHeader toggle={toggle}>
                <i className="mdi mdi-filter-variant me-1"></i>
                Filter Race Kits
            </ModalHeader>
            <ModalBody>
                <Row>
                    <Col md={6}>
                        <FormGroup>
                            <Label>Race</Label>
                            <Select
                                isMulti
                                placeholder="All races"
                                options={raceOptions}
                                value={selected(raceOptions, draft.races)}
                                onChange={(v) => set({ races: (v || []).map((o) => o.value) })}
                            />
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup>
                            <Label>Status</Label>
                            <Select
                                isMulti
                                placeholder="Any status"
                                options={raceKitStatusOptions}
                                value={selected(raceKitStatusOptions, draft.statuses)}
                                onChange={(v) => set({ statuses: (v || []).map((o) => o.value) })}
                            />
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup>
                            <Label>Club</Label>
                            <Select
                                isClearable
                                placeholder="All clubs"
                                options={clubOptions}
                                isLoading={clubsLoading}
                                value={clubOptions.filter((o) => o.value === draft.club)}
                                onChange={(v) => set({ club: v ? v.value : null })}
                            />
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup>
                            <Label>Tee Size</Label>
                            <Select
                                isMulti
                                placeholder="Any tee size"
                                options={teeSizeOptions}
                                value={selected(teeSizeOptions, draft.teeSizes)}
                                onChange={(v) => set({ teeSizes: (v || []).map((o) => o.value) })}
                            />
                            <small className="text-muted">A kit matches if any of its tees is this size, a parent's included.</small>
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup>
                            <Label>Bib Number</Label>
                            <div className="d-flex align-items-center">
                                <Input
                                    type="number"
                                    placeholder="From"
                                    value={draft.bibFrom}
                                    onChange={(e) => set({ bibFrom: e.target.value })}
                                />
                                <span className="mx-2">&ndash;</span>
                                <Input
                                    type="number"
                                    placeholder="To"
                                    value={draft.bibTo}
                                    onChange={(e) => set({ bibTo: e.target.value })}
                                />
                            </div>
                            <small className="text-muted">Numeric bibs only &mdash; lettered bibs are excluded by a range.</small>
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup>
                            <Label>Bib Issued</Label>
                            <div className="d-flex align-items-center">
                                <Input
                                    type="date"
                                    value={draft.bibIssuedFrom}
                                    onChange={(e) => set({ bibIssuedFrom: e.target.value })}
                                />
                                <span className="mx-2">&ndash;</span>
                                <Input
                                    type="date"
                                    value={draft.bibIssuedTo}
                                    onChange={(e) => set({ bibIssuedTo: e.target.value })}
                                />
                            </div>
                            <small className="text-muted">Only kits whose bib has been issued.</small>
                        </FormGroup>
                    </Col>
                    {canSeeNotes ? (
                        <Col md={6}>
                            <FormGroup>
                                <Label>Notes</Label>
                                <Select
                                    isClearable
                                    placeholder="Any"
                                    options={raceKitNotesOptions}
                                    value={raceKitNotesOptions.filter((o) => o.value === draft.notes)}
                                    onChange={(v) => set({ notes: v ? v.value : '' })}
                                />
                            </FormGroup>
                        </Col>
                    ) : null}
                </Row>
            </ModalBody>
            <ModalFooter>
                <Button color="light" onClick={() => setDraft(raceKitFiltersDefault)}>
                    <i className="mdi mdi-restore me-1"></i>Reset
                </Button>
                <Button color="secondary" onClick={toggle}>Cancel</Button>
                <Button color="primary" onClick={() => { onApply(draft); toggle(); }}>Apply</Button>
            </ModalFooter>
        </Modal>
    );
};
